<template>
  <BaseModal :model-value="modelValue" @update:model-value="emitOpen">
    <div class="tm">
      <header class="tm-head">
        <div>
          <h2>Transférer l'appel</h2>
          <p class="tm-sub">L'appel est mis en relation avec le destinataire choisi.</p>
        </div>
        <button class="tm-close" title="Fermer" @click="close"><Icon name="x" :size="16" /></button>
      </header>

      <!-- Saisie directe : extension interne ou numéro externe -->
      <form class="tm-direct" @submit.prevent="submitManual">
        <input
          ref="inputEl"
          v-model="manual"
          class="tm-input"
          placeholder="Extension ou numéro…"
          inputmode="tel"
          autocomplete="off"
          @input="onManualInput"
        />
        <button class="tm-go" type="submit" :disabled="!manualValid">
          <Icon name="forward" :size="14" /><span>Transférer</span>
        </button>
      </form>

      <!-- Sélection rapide d'un collègue -->
      <div class="tm-pick">
        <div class="tm-pick-head">
          <span class="tm-pick-title">Collègues</span>
          <input v-model="search" class="tm-search" placeholder="Rechercher…" />
        </div>

        <div v-if="loading" class="tm-state">Chargement…</div>
        <ul v-else-if="filtered.length" class="tm-list">
          <li v-for="a in filtered" :key="a.user_id">
            <button
              class="tm-agent"
              :disabled="!a.extension"
              :title="a.extension ? `Transférer vers ${a.display_name} (${a.extension})` : 'Aucune extension configurée'"
              @click="pick(a)"
            >
              <span class="tm-dot" :data-status="a.status"></span>
              <span class="tm-agent-name">{{ a.display_name }}</span>
              <span v-if="a.role !== 'agent'" class="tm-role" :data-role="a.role">{{ roleLabel(a.role) }}</span>
              <span class="tm-agent-ext mono">{{ a.extension || '—' }}</span>
              <span class="tm-agent-status">{{ statusLabel(a.status) }}</span>
              <Icon name="forward" :size="13" class="tm-agent-go" />
            </button>
          </li>
        </ul>
        <div v-else class="tm-state">{{ search ? 'Aucun collègue trouvé.' : 'Aucun collègue disponible.' }}</div>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import BaseModal from '@/shared/components/BaseModal.vue'
import Icon from '@/shared/components/Icon.vue'
import { QueueAPI } from '@/shared/api/queue'
import { useAuthStore } from '@/shared/stores/auth'

const props = defineProps<{ modelValue: boolean }>()
const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void
  (e: 'transfer', target: string): void
}>()

const auth = useAuthStore()

interface AgentRow {
  user_id: number
  display_name: string
  extension: string
  status: string
  role: string
}

const agents = ref<AgentRow[]>([])
const loading = ref(false)
const manual = ref('')
const search = ref('')
const inputEl = ref<HTMLInputElement | null>(null)

const manualValid = computed(() => manual.value.trim().length > 0)

// Tri : disponibles d'abord, puis par nom.
const STATUS_ORDER: Record<string, number> = { available: 0, wrap_up: 1, in_call: 2 }
const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  return agents.value
    .filter((a) => !q || a.display_name.toLowerCase().includes(q) || a.extension.includes(q))
    .sort((a, b) => {
      const oa = STATUS_ORDER[a.status] ?? 5
      const ob = STATUS_ORDER[b.status] ?? 5
      return oa - ob || a.display_name.localeCompare(b.display_name)
    })
})

const STATUS_LABELS: Record<string, string> = {
  available: 'Disponible', in_call: 'En appel', wrap_up: 'Post-appel',
  paused: 'En pause', break: 'Pause', away: 'Absent', training: 'Formation',
  offline: 'Hors ligne',
}
function statusLabel(s: string): string { return STATUS_LABELS[s] || s }

// Le transfert peut viser n'importe quel collègue avec un poste : agents,
// superviseurs, admins, owner. Le badge distingue les rôles d'encadrement.
const ROLE_LABELS: Record<string, string> = {
  team_admin: 'Superviseur', admin: 'Admin', owner: 'Propriétaire',
}
function roleLabel(r: string): string { return ROLE_LABELS[r] || r }

function onManualInput(e: Event) {
  // Garde chiffres, *, #, + (cohérent avec le composeur).
  manual.value = (e.target as HTMLInputElement).value.replace(/[^\d*#+]/g, '')
}

function emitOpen(v: boolean) { emit('update:modelValue', v) }
function close() { emit('update:modelValue', false) }

function submitManual() {
  if (!manualValid.value) return
  emit('transfer', manual.value.trim())
  close()
}
function pick(a: AgentRow) {
  if (!a.extension) return
  emit('transfer', a.extension)
  close()
}

async function loadAgents() {
  loading.value = true
  try {
    const rows = await QueueAPI.agents()
    agents.value = (rows || [])
      .filter((a: any) => a.user_id !== auth.user?.id) // pas soi-même
      .map((a: any) => ({
        user_id: a.user_id,
        display_name: a.display_name || a.username || `#${a.user_id}`,
        extension: a.extension || '',
        status: a.status || 'offline',
        role: a.role || 'agent',
      }))
  } catch {
    agents.value = []
  } finally {
    loading.value = false
  }
}

// À l'ouverture : recharge la liste et focus la saisie directe.
watch(
  () => props.modelValue,
  (open) => {
    if (!open) return
    manual.value = ''
    search.value = ''
    loadAgents()
    nextTick(() => inputEl.value?.focus())
  },
)
</script>

<style scoped>
.tm { display: flex; flex-direction: column; gap: 14px; width: min(420px, 86vw); }
.tm-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.tm-head h2 { margin: 0; font-size: 17px; font-weight: 700; }
.tm-sub { margin: 3px 0 0; font-size: 12.5px; color: var(--text-muted); }
.tm-close {
  width: 30px; height: 30px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: var(--surface-hover); border: 1px solid var(--border);
  border-radius: var(--radius); color: var(--text-muted); cursor: pointer;
}
.tm-close:hover { color: var(--text); border-color: var(--border-strong); }

.tm-direct { display: flex; gap: 8px; }
.tm-input {
  flex: 1; min-width: 0;
  padding: 10px 12px;
  border: 1px solid var(--border); border-radius: var(--radius);
  font-size: 14px; font-family: var(--font-mono); color: var(--text);
  background: var(--bg); outline: none;
}
.tm-input:focus { border-color: var(--primary); box-shadow: 0 0 0 3px var(--primary-100); }
.tm-go {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 10px 16px;
  background: var(--primary); color: #fff; border: none; border-radius: var(--radius);
  font-size: 13px; font-weight: 600; cursor: pointer; white-space: nowrap;
}
.tm-go:hover:not(:disabled) { background: var(--primary-hover); }
.tm-go:disabled { opacity: 0.45; cursor: not-allowed; }

.tm-pick { display: flex; flex-direction: column; gap: 8px; border-top: 1px solid var(--border); padding-top: 12px; }
.tm-pick-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.tm-pick-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-muted); }
.tm-search {
  width: 150px; padding: 6px 10px;
  border: 1px solid var(--border); border-radius: var(--radius-full);
  font-size: 12.5px; background: var(--surface); color: var(--text); outline: none;
}
.tm-search:focus { border-color: var(--primary); }

.tm-list {
  list-style: none; margin: 0; padding: 0;
  display: flex; flex-direction: column; gap: 4px;
  max-height: 280px; overflow-y: auto;
}
.tm-agent {
  width: 100%;
  display: flex; align-items: center; gap: 10px;
  padding: 9px 11px;
  background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius);
  cursor: pointer; text-align: left; transition: all var(--duration-fast);
}
.tm-agent:hover:not(:disabled) { border-color: var(--primary); background: var(--primary-50); }
.tm-agent:disabled { opacity: 0.5; cursor: not-allowed; }
.tm-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--text-muted); flex-shrink: 0; }
.tm-dot[data-status='available'] { background: var(--success); }
.tm-dot[data-status='in_call'], .tm-dot[data-status='wrap_up'] { background: var(--warning); }
.tm-dot[data-status='paused'], .tm-dot[data-status='break'], .tm-dot[data-status='away'] { background: var(--text-muted); }
.tm-agent-name { font-size: 13.5px; font-weight: 600; color: var(--text); flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tm-role {
  font-size: 10px; font-weight: 700; padding: 2px 7px; flex-shrink: 0;
  border-radius: var(--radius-full); text-transform: uppercase; letter-spacing: 0.03em;
  background: var(--warning-bg); color: var(--warning);
}
.tm-role[data-role='admin'] { background: var(--info-bg); color: var(--info); }
.tm-role[data-role='owner'] { background: var(--primary-100); color: var(--primary); }
.tm-agent-ext { font-size: 12.5px; color: var(--text-secondary); flex-shrink: 0; }
.tm-agent-status { font-size: 11px; color: var(--text-muted); flex-shrink: 0; width: 66px; text-align: right; }
.tm-agent-go { color: var(--text-disabled); flex-shrink: 0; }
.tm-agent:hover:not(:disabled) .tm-agent-go { color: var(--primary); }

.tm-state { padding: 18px; text-align: center; color: var(--text-muted); font-size: 13px; }
.mono { font-family: var(--font-mono); }
</style>
